import { getCategories } from "@api/egm";
import revenue from "@api/revenue";
import DownloadButton from "@buttons/DownloadButton";
import ExpandButton from "@buttons/ExpandButton";
import { defaultGrid } from "@helpers/datagrid";
import DataGrid from "@shared/DataGrid";
import DetailsButton from "@shared/DetailsButton";
import React from "react";
import { Button, Grid, Header, Loader, Segment } from "semantic-ui-react";
import RevenueFilters from "./RevenueFilters";
import RevenueGraph from "./RevenueGraph";

class BaseRevenueView extends React.Component {
  state = {
    expanded: false,
    selectedWeek: 8,
    selectedOperator: null,
    selectedCompany: -1,
    selectedSubCompany: -1,
    selectedViewType: null,
    selectedVarianceType: -1,
    selectedCategory: -1,
    selectedGroupBy: 0,
    selectedRegion: -1,
    selectedPromoType: -1,
    summary: defaultGrid,
    activeRow: {
      optionShowDetails: false,
    },
    graph: {
      lines: [],
      data: [],
    },
    categories: [],
    loading: false,
    exportLoading: false,
    chartLoading: false,
    operators: this.props.operators,
    regions: [],
    companies: [],
    subCompanies: [],
    viewTypes: [],
    graphOptions: [],
  };

  componentDidMount() {
    this.fnGetViewTypes().then((viewTypes) => {
      if (viewTypes.length < 1) {
        return;
      }

      this.setState({ viewTypes }, () => {
        this.onViewTypeChange(viewTypes[0].value);
      });
      this.handleOperatorChange(this.props.operators[0].value);
    });
  }

  // Reset selectedRegion value when selectedGroupBy change
  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.selectedGroupBy !== prevState.selectedGroupBy &&
      this.state.selectedGroupBy !== 0
    ) {
      this.setState({ selectedRegion: -1 });
    }
  }

  onRowClick = (activeRow) => {
    this.setState({ activeRow, chartLoading: true }, () => this.refreshGraph());
  };

  onGraphTypeChange = (selectedVarianceType) => {
    this.setState({ selectedVarianceType }, () => this.refreshGraph());
  };

  handleOperatorChange = (selectedOperator) => {
    this.setState({ selectedOperator, selectedCategory: -1 }, () => {
      revenue
        .getRegions(selectedOperator)
        .then((regions) => this.setState({ regions }));
      getCategories(selectedOperator).then((categories) =>
        this.setState({ categories }),
      );
      revenue.getCompanies(selectedOperator).then((companies) => {
        const selectedCompany =
          companies.length > 0 ? companies[0].value : null;
        this.setState({ companies, selectedCompany });
      });
    });
  };

  handleCompanyChange = (selectedCompany) => {
    revenue.getSubCompanies(selectedCompany).then((subCompanies) => {
      const selectedSubCompany =
        subCompanies.length > 0 ? subCompanies[0].value : null;
      this.setState({ subCompanies, selectedCompany, selectedSubCompany });
    });
  };

  onViewTypeChange = (selectedViewType) => {
    this.fnGetGraphOptions(selectedViewType).then((graphOptions) => {
      const selectedVarianceType =
        graphOptions.length > 0 ? graphOptions[0].value : -1;
      this.setState({
        selectedViewType,
        graphOptions,
        activeRow: { optionShowDetails: false },
        selectedVarianceType,
        graph: { lines: [], data: [] },
      });
    });
  };

  onFilterChange = (key, value) => this.setState({ [key]: value });

  render() {
    const {
      summary,
      graph,
      selectedVarianceType,
      activeRow,
      loading,
      chartLoading,
      expanded,
      detailsLoading,
      graphOptions,
      exportLoading,
    } = this.state;

    return (
      <div className="height-100 gs-flex-container-vertical">
        <Header size="small" className="my-0">
          Summary Dashboard
          <Button
            floated="right"
            compact
            icon="refresh"
            positive
            onClick={this.handleRefresh}
            content="Refresh"
            loading={loading}
            disabled={loading}
          />
          <DetailsButton
            floated="right"
            compact
            loading={detailsLoading}
            disabled={!activeRow.optionShowDetails}
            onClick={this.onDetailsClick}
          />
          <ExpandButton
            size="medium"
            active={expanded}
            content={expanded ? "Display Graph" : "Hide Graph"}
            onClick={() => this.setState({ expanded: !expanded })}
          />
          <DownloadButton
            caption="Export"
            loading={exportLoading}
            onClick={this.onExportClick}
            floated="right"
            size="medium"
            compact
          />
        </Header>
        <Segment className="flex-stretch my-0_5">
          <Grid className="gs-flex-container-vertical height-100">
            <Grid.Row className="flex-shrink">
              <Grid.Column width="16" verticalAlign="middle">
                <RevenueFilters
                  {...this.state}
                  useGroupBy={this.useGroupBy}
                  viewTypes={this.state.viewTypes}
                  onViewTypeChange={this.onViewTypeChange}
                  isVariance={this.isVariance}
                  varianceTypes={graphOptions}
                  onOperatorChange={this.handleOperatorChange}
                  onCompanyChange={this.handleCompanyChange}
                  onChange={this.onFilterChange}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className="flex-stretch">
              <Grid.Column className="gs-flex-container-vertical">
                <DataGrid
                  groupHeadings={summary.span}
                  headings={summary.headerDef}
                  activeRow={activeRow}
                  data={summary.data}
                  loading={loading}
                  rowDef={summary.rowDef}
                  hideOverflow
                  onRowClick={this.onRowClick}
                  fullHeight
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {!expanded && (
          <Segment className="flex-shrink">
            <Loader active={chartLoading} />
            <div className="flex-vertical h-100">
              {graphOptions.length > 0 && (
                <div
                  className="flex-shrink mr-1"
                  style={{ textAlign: "center" }}
                >
                  <div className="mb-0_5" style={{ fontWeight: "bold" }}>
                    Data Type:
                  </div>
                  <Button.Group basic vertical>
                    {graphOptions.map((option) => {
                      return (
                        <Button
                          disabled={!activeRow.siteId}
                          active={selectedVarianceType === option.value}
                          onClick={() => this.onGraphTypeChange(option.value)}
                        >
                          {option.text}
                        </Button>
                      );
                    })}
                  </Button.Group>
                </div>
              )}
              <div className="flex-stretch flex-container">
                <div
                  style={{
                    border: "1px solid #ddd",
                    padding: "1em 1em 0em 2em",
                  }}
                >
                  <Loader active={chartLoading} />
                  <RevenueGraph
                    data={graph.data}
                    lines={graph.lines}
                    symbol={graph.symbol}
                  />
                </div>
              </div>
            </div>
          </Segment>
        )}
      </div>
    );
  }
}

export default BaseRevenueView;
